/**
 * Reprise de la dernière page (retour du 12/10/2026 : « quand on actualise ou
 * qu'on reste sur la même page, ça retourne souvent dans l'accueil »).
 *
 * L'application installée (PWA ou Capacitor) est fermée par le téléphone dès
 * qu'elle passe en arrière-plan un moment ; en revenant, le système la relance
 * sur son adresse de départ, l'accueil. On retient donc la dernière page
 * interne ouverte, par compte, et on la rouvre au démarrage de l'application
 * installée. Un navigateur ordinaire garde déjà l'adresse au rechargement : il
 * n'est pas concerné, pour ne pas détourner qui ouvre volontairement l'accueil.
 */
const KEY = 'myequip.last-page';
/** Au-delà, rouvrir une vieille page surprendrait plus qu'elle n'aiderait. */
const MAX_AGE_MS = 12 * 3_600_000;

interface LastPage {
  userId: string;
  path: string;
  at: number;
}

function installedApp(): boolean {
  if (typeof window === 'undefined') return false;
  const capacitor = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as { standalone?: boolean }).standalone === true ||
    capacitor?.isNativePlatform?.() === true
  );
}

export function rememberPage(userId: string, path: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ userId, path, at: Date.now() } satisfies LastPage));
  } catch {
    // Stockage indisponible (navigation privée) : la reprise est simplement absente.
  }
}

/** Page à rouvrir au lancement, si l'application installée démarre sur l'accueil. */
export function pageToResume(userId: string, now = Date.now()): string | undefined {
  if (!installedApp()) return undefined;
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? 'null') as LastPage | null;
    if (
      !saved ||
      saved.userId !== userId ||
      typeof saved.path !== 'string' ||
      now - saved.at > MAX_AGE_MS ||
      // Uniquement un chemin interne de l'application, jamais une adresse externe.
      !saved.path.startsWith('/') ||
      saved.path.startsWith('//') ||
      saved.path === '/'
    )
      return undefined;
    return saved.path;
  } catch {
    return undefined;
  }
}
